import React, { useState, useEffect } from "react";
import { GridComponent, Heading, VatContainer } from "components/VatReturn";
import VatDetails from "./VatDetails";
import * as api from '../../api/index'
import { AmountFormat, DateFormatFull, formatDateForAPI } from "components/common/formatter";
import { Switch } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import styles from "../../components/VatReturn/VatReturn.module.scss"

const VatReturn = () => {
    const [showDetails, setShowDetails] = useState(false);
    const [vatData, setVatData] = useState([]);
    const [completedVatData, setCompletedVatData] = useState([]);
    const [item, setItem] = useState({});
    const [hmrcMtd, setHmrcMtd] = useState(true);
    const [toDate, setToDate] = useState(new Date());

    const dataAnalyzer = (item, type) => {
        return {
            fromDate: formatDateForAPI(new Date(item.fromDate)),
            toDate: formatDateForAPI(new Date(item.toDate)),
            period: `${DateFormatFull(item.fromDate)} - ${DateFormatFull(item.dueDate)}`,
            amount: item.vatAmount,
            submissionDetails: type === 'completed' && `Submitted on ${DateFormatFull(item.datePosted)} by ${item.postedByFullName}`,
        }
    }

    const updateData = async () => {
        try {
            const data = await api.getVatReturnHistory()
            data.data?.data?.map((item) => {
                const tempItem = dataAnalyzer(item, 'completed')
                setCompletedVatData(prevItem => [...prevItem, tempItem])
            })
            const dataHistory = await api.getVatObligations()
            dataHistory.data?.data?.obligations?.map((item) => {
                const tempItem = dataAnalyzer({
                    fromDate: item.start,
                    toDate: item.end,
                    dueDate: item.due,
                    vatAmount: item.vatAmount,
                }, 'current')
                setVatData(prevItem => [...prevItem, tempItem])
            })
        } catch (error) {
            console.log(error)
        }
    };

    useEffect(() => {
        updateData()
    }, []);

    const selectedToDateStr = toDate ? formatDateForAPI(toDate) : undefined;

    const openVatDetails = (props, customToDate) => {
        setItem(customToDate ? { ...props, toDate: customToDate } : props);
        setShowDetails(true);
    };

    return showDetails === false ?
        <div className={`${styles['vat-return-container']}`}>
            <div className="row justify-content-end mb-1">
                <div className="col-auto d-flex align-items-center gap-2">
                    <Switch
                        checked={hmrcMtd}
                        onChange={(e) => setHmrcMtd(e.value)}
                        size="small"
                    />
                    <span className={styles['text-light-color']}>HMRC MTD</span>
                </div>
            </div>

            <div className="row">
                <div className="col-4">
                    <VatContainer style={{ borderRadius: '10px 0px 0px 10px' }}>
                        <p className={styles['text-light-color']}>VAT amount refundable</p>
                        <p className="fs-md-3 fw-semibold">Nothing to pay</p>
                    </VatContainer>
                </div>
                <div className="col-4">
                    <VatContainer>
                        <p className={styles['text-light-color']}>Payment due</p>
                        <p className="fs-md-3 fw-semibold">None</p>
                        <u><p className="fs-sm">View previous payments</p></u>
                    </VatContainer>
                </div>
                <div className="col-4">
                    <VatContainer style={{ borderRadius: '0px 10px 10px 0px' }}>
                        <p className={styles['text-light-color']}>Payment method</p>
                        <p className="fs-md-3 fw-semibold">Refund from HMRC</p>
                    </VatContainer>
                </div>
            </div>

            <div className="row mt-2 align-items-center">
                <div className="col-auto">
                    <Heading name={'Current'} />
                </div>
                {!hmrcMtd && (
                    <div className="col-auto">
                        <div className={styles['date-picker-container']}>
                            <p className={styles['date-label']}>To Date:</p>
                            <DatePicker
                                value={toDate}
                                onChange={(e) => setToDate(e.value)}
                                format="dd/MM/yyyy"
                                toggleButton={CalenderCustomToggleButton}
                                className={styles['to-date-picker']}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="row mt-2" style={hmrcMtd ? { flex: '1 1 0', minHeight: 0 } : { flex: '0 0 auto' }}>
                <div className={`col-12 ${hmrcMtd ? 'h-100' : ''}`}>
                    <div className={styles['current-section-scroll']}>
                        {!hmrcMtd ? (
                            <div className={styles['current-vat-card']}>
                                <div>
                                    <p className={styles['date-text']}>
                                        Up to: {DateFormatFull(toDate)}
                                    </p>
                                    {vatData[0]?.amount != null && vatData[0]?.amount !== 0 && (
                                        <p className={styles['text-light-color']}>
                                            Due:
                                            <span className={styles['due-amount']}>
                                                {AmountFormat(vatData[0].amount)}
                                            </span>
                                        </p>
                                    )}
                                </div>
                                <Button
                                    className={`common-btn ${styles['grid-btn']}`}
                                    onClick={() => openVatDetails({ fromDate: vatData[0]?.fromDate }, selectedToDateStr)}
                                >
                                    Review
                                </Button>
                            </div>
                        ) : (
                            <GridComponent data={vatData} showDetails={openVatDetails} />
                        )}
                    </div>
                </div>
            </div>

            <div className="row mt-2">
                <Heading name={'Completed'} />
            </div>
            <div className="row mt-2" style={{ flex: '2 1 0', minHeight: 0 }}>
                <div className="col-12 h-100">
                    <div className={styles['completed-vat-scroll']}>
                        <GridComponent data={completedVatData} showDetails={openVatDetails} />
                    </div>
                </div>
            </div>

            <div className="row mt-3">
                <div className="col-12">
                    <p className={styles['text-light-color']}>
                        *By remaining connected to the HMRC MTD system, the Linq Legal service will engage in on-going communications with HMRC systems. These communications include sending submission contents, and browser data (such as IP address and other metadata) as is legally required to be sent to HMRC for security and fraud prevention purposes. To learn more, including how to disconnect your Linq Legal accounts from the HMRC MTD system
                    </p>
                </div>
            </div>
        </div> :
        <VatDetails onBack={() => { setShowDetails(false) }} item={item} hmrcMtd={hmrcMtd} />

}

export default VatReturn;
